import { Fragment, useEffect, useState, useMemo } from "react";
import {
  PDFViewer,
  Document,
  Page,
  Text,
  View,
  pdf, // ✅ Added for blob generation
} from "@react-pdf/renderer";
import styles from "./PDFReportViewer.module.css";
import styled from "styled-components";
import Button from "../../components/Button";
import { registerPoppins } from "../../fonts/registerFonts";
import backIcon from "../../assets/back_icon.svg";
import AntRadioGroup from "../../components/Ant/AntRadioGroup/AntRadioGroup";
import { useTranslation } from "react-i18next";
import { getLocalizedDate } from "../../functions/localization";
import { Pagination } from "antd";

registerPoppins();

const MAX_PREVIEW_ROWS = 10;
const ROWS_PER_PDF_PAGE = 40;

const MyView = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 30px 20px;
  height: 100%;
  max-height: 1024px;
  min-height: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 75px;
  min-height: 75px;
  border-bottom: 1px solid #a7bbcf;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  width: 304px;
  min-width: 304px;
`;

const BackButton = styled.img`
  cursor: pointer;
  width: 30px;
  height: 30px;
  margin-right: 20px;
`;

const ReportTitle = styled.p`
  margin: 0;
  font-family: "Poppins";
  font-style: normal;
  font-weight: normal;
  font-size: 24px;
  line-height: 33px;
  color: #454b60;
  min-width: 160px;
  width: 160px;
`;

const PDFReportViewer = ({
  user,
  title,
  headings,
  groupedField,
  data,
  collectionSequence,
  onBack,
  columnSums,
  columnAverages,
  selectionRange,
  showAVGRow,
  showSUMRow,
  headingsSpacing,
  dataSpacing,
  defaultFontSize,
}) => {
  const { t } = useTranslation("pdfReportViewer");

  const pageWidth = 760;
  const columnWidth = pageWidth / headings.length;
  const charWidthInPx = 6.4;

  const [columnWidthTypeRadioValue, setColumnWidthTypeRadioValue] = useState(1);
  const [fontSizeRadioValue, setFontSizeRadioValue] = useState(() =>
    defaultFontSize?.toLowerCase() === "small" ? 2 : 1,
  );
  const [headingsAndDataFontSize, setHeadingsAndDataFontSize] = useState(() =>
    defaultFontSize?.toLowerCase() === "small" ? 8 : 10,
  );
  const [sumAndAvgFontSize, setSumAndAvgFontSize] = useState(() =>
    defaultFontSize?.toLowerCase() === "small" ? 10 : 12,
  );

  let allowCustomWidths =
    headingsSpacing?.length === headings.length &&
    dataSpacing?.length === headings.length;

  useEffect(() => {
    if (allowCustomWidths) {
      setColumnWidthTypeRadioValue(3);
    }
  }, []);

  const tempVariableColumnWidthsArray = headings.map((heading) => {
    const parts = heading.trim().split(" ");
    const maxLength = Math.max(...parts.map((part) => part.length));
    return maxLength * charWidthInPx;
  });

  let tableRowsData =
    !groupedField || groupedField === "No grouping"
      ? data
      : data.flatMap((group) => group);

  tableRowsData.forEach((row, i) => {
    row.forEach((val, j) => {
      const strVal =
        typeof val === "string" ? val.substring(0, 750) : val.toString();
      const substrings = strVal.split("\n");
      substrings.forEach((s) => {
        const width = Math.min(s.length * charWidthInPx, 200);
        if (width > tempVariableColumnWidthsArray[j]) {
          tempVariableColumnWidthsArray[j] = width;
        }
      });
    });
  });

  const totalColumnWidth = tempVariableColumnWidthsArray.reduce(
    (sum, w) => sum + w,
    0,
  );
  const proportionalityCorrectionFactor = 758 / totalColumnWidth;
  const variableColumnWidthsArray = tempVariableColumnWidthsArray.map(
    (w) => w * proportionalityCorrectionFactor,
  );

  let startDate = selectionRange?.startDate
    ? getLocalizedDate(
        selectionRange.startDate,
        user?.defaultSite?.shippingAddress?.country,
      )
    : null;
  let endDate = selectionRange?.endDate
    ? getLocalizedDate(
        selectionRange.endDate,
        user?.defaultSite?.shippingAddress?.country,
      )
    : null;

  const [currentPage, setCurrentPage] = useState(0);
  const previewData = useMemo(
    () =>
      data.slice(
        currentPage * MAX_PREVIEW_ROWS,
        (currentPage + 1) * MAX_PREVIEW_ROWS,
      ),
    [data, currentPage],
  );
  const fontSize = defaultFontSize === "small" ? 8 : 10;

  const createPDFDocument = ({
    title,
    headings,
    data,
    columnWidth,
    fontSize,
    startDate,
    endDate,
    isDownload = false,
  }) => {
    const pages = [];

    const headerView = (
      <View
        fixed
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          fontSize: 10,
          fontWeight: 500,
          marginBottom: 10,
        }}
      >
        <Text>{title}</Text>
        <Text>{startDate && endDate ? `${startDate} - ${endDate}` : ""}</Text>
      </View>
    );

    const headingsRow = (
      <View
        fixed
        style={{
          flexDirection: "row",
          borderBottom: "2px solid #089BAB",
          paddingVertical: 12,
        }}
      >
        {headings.map((heading, i) => {
          let width = columnWidth;
          if (columnWidthTypeRadioValue === 1)
            width = variableColumnWidthsArray[i];
          else if (columnWidthTypeRadioValue === 3)
            width = headingsSpacing[i].columnWidth;

          return (
            <Text
              key={i}
              style={{
                width,
                fontSize: headingsAndDataFontSize,
                fontWeight: 500,
                paddingRight: 5,
              }}
            >
              {heading}
            </Text>
          );
        })}
      </View>
    );

    for (let i = 0; i < data.length; i += ROWS_PER_PDF_PAGE) {
      const pageRows = data.slice(i, i + ROWS_PER_PDF_PAGE);
      const isLastPage = i + ROWS_PER_PDF_PAGE >= data.length;

      pages.push(
        <Page
          key={i}
          size="LETTER"
          orientation="landscape"
          style={{ padding: 20, fontFamily: "Poppins" }}
        >
          {headerView}
          {headingsRow}
          {pageRows.map((row, rIdx) => (
            <View
              key={rIdx}
              style={{
                flexDirection: "row",
                backgroundColor: rIdx % 2 ? "#EBEDF8" : "#FFFFFF",
                paddingVertical: 5,
              }}
            >
              {row.map((cell, cIdx) => {
                let width = columnWidth;
                if (columnWidthTypeRadioValue === 1)
                  width = variableColumnWidthsArray[cIdx];
                else if (columnWidthTypeRadioValue === 3)
                  width = dataSpacing[cIdx].columnWidth;

                return (
                  <Text
                    key={cIdx}
                    style={{
                      width,
                      fontSize: headingsAndDataFontSize,
                      paddingRight: 5,
                    }}
                  >
                    {typeof cell === "string"
                      ? cell.replace(/-/g, "\u00AD")
                      : cell}
                  </Text>
                );
              })}
            </View>
          ))}
          <Text
            fixed
            style={{
              position: "absolute",
              bottom: 6,
              width: "100%",
              textAlign: "center",
              fontSize: 10,
            }}
            render={({ pageNumber, totalPages }) =>
              `${pageNumber} / ${totalPages}`
            }
          />

          {/* SUM/AVG rows (optional) */}
          {isDownload && isLastPage && (
            <Fragment>
              {showSUMRow && (
                <>
                  <Text
                    style={{
                      fontSize: sumAndAvgFontSize,
                      fontWeight: 500,
                      color: "#089BAB",
                      paddingTop: 12,
                    }}
                  >
                    {t("sumCol")}
                  </Text>
                  <View style={{ flexDirection: "row", paddingTop: 10 }}>
                    {columnSums.map((total, i) => (
                      <Text
                        key={i}
                        style={{
                          width: variableColumnWidthsArray[i],
                          fontSize: sumAndAvgFontSize,
                          paddingRight: 5,
                        }}
                      >
                        {total >= 0
                          ? total.toFixed(2).replace(/(\.\d*[1-9])0+$/, "$1")
                          : "--"}
                      </Text>
                    ))}
                  </View>
                </>
              )}
              {showAVGRow && (
                <>
                  <Text
                    style={{
                      fontSize: sumAndAvgFontSize,
                      fontWeight: 500,
                      color: "#089BAB",
                      paddingTop: 12,
                    }}
                  >
                    {t("averageCol")}
                  </Text>
                  <View style={{ flexDirection: "row", paddingTop: 10 }}>
                    {columnAverages.map((total, i) => (
                      <Text
                        key={i}
                        style={{
                          width: variableColumnWidthsArray[i],
                          fontSize: sumAndAvgFontSize,
                          paddingRight: 5,
                        }}
                      >
                        {total >= 0
                          ? total.toFixed(2).replace(/(\.\d*[1-9])0+$/, "$1")
                          : "--"}
                      </Text>
                    ))}
                  </View>
                </>
              )}
            </Fragment>
          )}
        </Page>,
      );
    }

    return <Document title={title}>{pages}</Document>;
  };

  const previewDoc = useMemo(() => {
    return createPDFDocument({
      title,
      headings,
      data: previewData,
      columnWidth,
      fontSize,
      startDate,
      endDate,
      isDownload: false,
    });
  }, [
    title,
    headings,
    previewData,
    columnWidth,
    fontSize,
    startDate,
    endDate,
    columnWidthTypeRadioValue,
    headingsAndDataFontSize,
  ]);

  // ✅ New async download function
  const handleDownload = async () => {
    const fullDocInstance = createPDFDocument({
      title,
      headings,
      data,
      columnWidth,
      fontSize,
      startDate,
      endDate,
      isDownload: true,
    });

    const asPdf = pdf([]);
    asPdf.updateContainer(fullDocInstance);
    const blob = await asPdf.toBlob();
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/\s+/g, "_")}_Report.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <MyView>
      <Header>
        <TitleGroup>
          <BackButton src={backIcon} onClick={onBack} />
          <ReportTitle>{t("title")}</ReportTitle>
          <AntRadioGroup
            values={allowCustomWidths ? [1, 2, 3] : [1, 2]}
            labels={
              allowCustomWidths
                ? [
                    t("variableColumnLabel"),
                    t("equalColumnLabel"),
                    t("customColumnLabel"),
                  ]
                : [t("variableColumnLabel"), t("equalColumnLabel")]
            }
            radioGroupClass="ant-radioGroup-000"
            onChange={(e) => setColumnWidthTypeRadioValue(e.target.value)}
            value={columnWidthTypeRadioValue}
          />
          <div className={styles.PDFReportViewer__fontSizeRadioGroupContainer}>
            <AntRadioGroup
              values={[1, 2]}
              labels={[t("largeFontLabel"), t("smallFontLabel")]}
              radioGroupClass="ant-radioGroup-000"
              onChange={(e) => {
                const value = e.target.value;
                setFontSizeRadioValue(value);
                setHeadingsAndDataFontSize(value === 1 ? 10 : 8);
                setSumAndAvgFontSize(value === 1 ? 12 : 10);
              }}
              value={fontSizeRadioValue}
            />
          </div>
        </TitleGroup>

        {/* ✅ Button triggers full PDF generation on demand */}
        <Button
          onClick={handleDownload}
          labelName="Download PDF"
          isPrimary={true}
        >
          Download PDF
        </Button>
      </Header>

      <PDFViewer width="100%" height="86%">
        {previewDoc}
      </PDFViewer>

      <div
        style={{
          marginTop: "20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <Pagination
          defaultCurrent={1}
          total={data.length}
          pageSize={10}
          current={currentPage + 1}
          onChange={(page) => setCurrentPage(page - 1)}
          showSizeChanger={false}
        />
      </div>
    </MyView>
  );
};

export default PDFReportViewer;
